import { ArrowLeftIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useCreateDonationsCampaignMutation,
  useGetDonationsCampaignDetailQuery,
  useUpdateDonationsCampaignMutation,
} from "../../../state/domainRouterApi";
import { TailwindDateField } from "../../../components/forms/TailwindDateField";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";

const PRIMARY = "#2697a2";
const TEXT = "#404f4d";
const BORDER = "#e5e7eb";

type CampaignFormState = {
  name: string;
  campaignType: string;
  startDate: string;
  endDate: string;
  priceCents: string;
  blackoutDates: string;
  slug: string;
};

const EMPTY_FORM: CampaignFormState = {
  name: "",
  campaignType: "general",
  startDate: "",
  endDate: "",
  priceCents: "",
  blackoutDates: "",
  slug: "",
};

const CAMPAIGN_TYPES = [
  { value: "general", label: "General Donations" },
  { value: "annual-fund", label: "Annual Fund" },
  { value: "parnes-hayom", label: "Parnes Hayom" },
];

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((index) => (
        <div
          key={index}
          className="h-14 animate-pulse rounded-lg bg-gray-100"
        />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return <SupportErrorMessage>{message}</SupportErrorMessage>;
}

function FormField({
  label,
  htmlFor,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block text-sm font-medium"
        style={{ color: TEXT }}
      >
        {label}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </label>
      {children}
    </div>
  );
}

const inputClasses =
  "block w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2";

export function SchoolDonationsCampaignEditorPage() {
  const { campaignId } = useParams<{ campaignId?: string }>();
  const isEditing = Boolean(campaignId);

  const {
    data: existingCampaign,
    isLoading: loadingDetail,
    isError: detailError,
    error: detailErrorObj,
  } = useGetDonationsCampaignDetailQuery(
    { campaignId: campaignId ?? "" },
    { skip: !campaignId },
  );

  const [createCampaign, { isLoading: creating }] =
    useCreateDonationsCampaignMutation();
  const [updateCampaign, { isLoading: updating }] =
    useUpdateDonationsCampaignMutation();

  const [form, setForm] = useState<CampaignFormState>(EMPTY_FORM);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const isParnesHayomCampaign = form.campaignType === "parnes-hayom";

  useEffect(() => {
    if (!existingCampaign || typeof existingCampaign !== "object") return;
    const campaign = existingCampaign as Record<string, unknown>;
    setForm({
      name: String(campaign.name ?? ""),
      campaignType: String(campaign.campaignType ?? "general"),
      startDate: String(campaign.startDate ?? ""),
      endDate: String(campaign.endDate ?? ""),
      priceCents:
        campaign.priceCents != null ? String(campaign.priceCents) : "",
      blackoutDates: Array.isArray(campaign.blackoutDates)
        ? (campaign.blackoutDates as string[]).join(", ")
        : String(campaign.blackoutDates ?? ""),
      slug: String(campaign.slug ?? ""),
    });
  }, [existingCampaign]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.target;
      setForm((current) => ({ ...current, [name]: value }));
      setSubmitError(null);
      setSubmitSuccess(false);
    },
    [],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    const input: Record<string, unknown> = {
      name: form.name.trim(),
      campaignType: form.campaignType,
      startDate: form.startDate,
      endDate: form.endDate,
      slug: form.slug.trim() || undefined,
    };

    if (!isParnesHayomCampaign && form.priceCents.trim()) {
      const parsed = Number.parseInt(form.priceCents.trim(), 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        input.priceCents = parsed;
      }
    }

    if (form.blackoutDates.trim()) {
      input.blackoutDates = form.blackoutDates
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);
    }

    try {
      if (isEditing && campaignId) {
        await updateCampaign({ campaignId, ...input }).unwrap();
      } else {
        await createCampaign(input).unwrap();
      }
      setSubmitSuccess(true);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : err && typeof err === "object" && "message" in err
            ? String((err as { message?: unknown }).message)
            : "Failed to save campaign. The donations backend may not be deployed.";
      setSubmitError(message);
    }
  };

  const submitting = creating || updating;

  return (
    <main
      data-testid="school-donations-campaign-editor"
      data-route-ready="true"
      data-extracted-from="source-faithful:school-donations-v1.0"
      className="min-h-screen bg-[var(--background-light)]"
    >
      <div className="mx-auto max-w-[720px] space-y-6">
        <a
          href="/school/donations"
          className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
          style={{ color: PRIMARY }}
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          Back to Dashboard
        </a>

        <div>
          <h2 className="text-[24px] font-semibold" style={{ color: TEXT }}>
            {isEditing ? "Edit Campaign" : "New Campaign"}
          </h2>
        </div>

        {loadingDetail ? (
          <LoadingSkeleton />
        ) : detailError ? (
          <ErrorBanner
            message={
              (detailErrorObj &&
              typeof detailErrorObj === "object" &&
              "message" in detailErrorObj
                ? String((detailErrorObj as { message?: unknown }).message)
                : null) ??
              "Campaign data is not yet available. The donations backend has not been deployed."
            }
          />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-5 rounded-xl border bg-white p-6 shadow-sm"
            style={{ borderColor: BORDER }}
          >
            <FormField label="Campaign Name" htmlFor="name" required>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={handleChange}
                className={inputClasses}
                style={{ borderColor: BORDER, color: TEXT }}
                placeholder="Annual Fund Drive 2026"
              />
            </FormField>

            <FormField label="Campaign Type" htmlFor="campaignType" required>
              <select
                id="campaignType"
                name="campaignType"
                required
                value={form.campaignType}
                onChange={handleChange}
                className={inputClasses}
                style={{ borderColor: BORDER, color: TEXT }}
              >
                {CAMPAIGN_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </FormField>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Start Date" htmlFor="startDate" required>
                <TailwindDateField
                  id="startDate"
                  name="startDate"
                  required
                  value={form.startDate}
                  onChange={handleChange}
                  className={inputClasses}
                  style={{ borderColor: BORDER, color: TEXT }}
                />
              </FormField>

              <FormField label="End Date" htmlFor="endDate" required>
                <TailwindDateField
                  id="endDate"
                  name="endDate"
                  required
                  value={form.endDate}
                  onChange={handleChange}
                  className={inputClasses}
                  style={{ borderColor: BORDER, color: TEXT }}
                />
              </FormField>
            </div>

            <FormField
              label={
                isParnesHayomCampaign
                  ? "Default Price (managed in General Settings)"
                  : "Default Price (cents)"
              }
              htmlFor="priceCents"
            >
              <input
                id="priceCents"
                name="priceCents"
                type="number"
                min="0"
                step="1"
                value={form.priceCents}
                onChange={handleChange}
                disabled={isParnesHayomCampaign}
                className={inputClasses}
                style={{
                  borderColor: BORDER,
                  color: TEXT,
                  backgroundColor: isParnesHayomCampaign ? "#f9fafb" : "white",
                }}
                placeholder="18000"
              />
              {isParnesHayomCampaign ? (
                <p className="mt-2 text-xs" style={{ color: `${TEXT}B3` }}>
                  Parnes HaYom sponsorship pricing now comes from{" "}
                  <span className="font-semibold">school/settings/general</span>
                  . This campaign record keeps the calendar window only.
                </p>
              ) : null}
            </FormField>

            <FormField
              label="Blackout Dates (comma-separated)"
              htmlFor="blackoutDates"
            >
              <input
                id="blackoutDates"
                name="blackoutDates"
                type="text"
                value={form.blackoutDates}
                onChange={handleChange}
                className={inputClasses}
                style={{ borderColor: BORDER, color: TEXT }}
                placeholder="2026-03-14, 2026-04-13"
              />
            </FormField>

            <FormField label="URL Slug" htmlFor="slug">
              <input
                id="slug"
                name="slug"
                type="text"
                value={form.slug}
                onChange={handleChange}
                className={inputClasses}
                style={{ borderColor: BORDER, color: TEXT }}
                placeholder="annual-fund-2026"
              />
            </FormField>

            {submitError ? <ErrorBanner message={submitError} /> : null}

            {submitSuccess ? (
              <div
                className="rounded-xl border px-6 py-4 text-sm font-medium"
                style={{
                  borderColor: "#86efac",
                  backgroundColor: "#f0fdf4",
                  color: "#166534",
                }}
              >
                Campaign saved successfully.
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ backgroundColor: PRIMARY }}
            >
              {submitting
                ? "Saving..."
                : isEditing
                  ? "Update Campaign"
                  : "Create Campaign"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
